import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, Typography } from '@mui/material';
import useFlexBoxStyles from 'common/flexBox';
import useOnliveStyles from 'common/onliveStyles';
import ActionButton from '../../common/components/ActionButton';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import OlIcon from '@onlive.site/ui/dist/react/icon';
import parse from 'html-react-parser';
import useFontStyles from '../../common/fontStyles';


const useStyles = makeStyles()((theme) => ({
    paper: {
        maxWidth: 'unset',
        width: 'min-content',
        [theme.breakpoints.down('sm')]: {
            width: 'unset',
        },
    },
    title: {
        flexGrow: 1,
    },
    message: {
        [theme.breakpoints.up('sm')]: {
            minWidth: theme.spacing(60),
        },
    },
    options: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
        paddingLeft: theme.spacing(3),
        [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap',
        },
    },
}));

export default function ConfirmationOptionsDialog({
    title,
    message,
    options: optionsProps,
    onOptionConfirmation,
}) {
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: onlive } = useOnliveStyles();
    const { classes: font } = useFontStyles();
    
    const [timer, setTimer] = useState(0);

    const { t } = useTranslation();

    const options = useMemo(
        () => (optionsProps.length ? optionsProps : [{ label: t('common.term.OK'), value: 'ok' }]),
        [optionsProps, t],
    );

    const delay = useMemo(() => options.reduce((current, option) => {
        if (typeof option.delay === 'number' && option.delay > current) {
            return option.delay;
        }
        return current;
    }, 0), [options]);

    useEffect(() => {
        if (timer < delay) {
            const timeout = setTimeout(() => setTimer(timer + 1), 1000);
            return () => clearTimeout(timeout);
        }
    }, [delay, timer]);

    const closeOption = options.find((option) => option.close);

    return (
        <Dialog classes={{ paper: cx(onlive.boxShadow, classes.paper) }} open>
            <DialogContent className={cx(flexBox.flex, flexBox.column, flexBox.gap2)}>
                {title || closeOption ? (
                    <div className={cx(flexBox.flex, flexBox.alignItemsCenter)}>
                        <Typography variant="onliveH5" fontWeight="500" className={classes.title}>
                            {title}
                        </Typography>
                        {closeOption && (!closeOption.delay || closeOption.delay <= timer) ? (
                            <OlIcon
                                name="x-lg"
                                className={flexBox.cursorPointer}
                                onClick={() => onOptionConfirmation(closeOption.value)}
                            />
                        ) : null}
                    </div>
                ) : null}
                <Typography variant="subtitle2" fontWeight={400} className={classes.message}>
                    {parse(message)}
                </Typography>
                <div className={classes.options}>
                    {options.map((option, index) => {
                        if (option) {
                            const delayed = typeof option.delay === 'number' && Math.max(0, option.delay - timer);
                            return (
                                <ActionButton
                                    key={index}
                                    variant={option.variant || 'default'}
                                    onClick={
                                        delayed || !onOptionConfirmation
                                            ? undefined
                                            : () => onOptionConfirmation(option.value)
                                    }
                                    additionalClass={flexBox.margin0}
                                    disabled={!!delayed}
                                    fitWidth
                                >
                                    {option.label}
                                    {option.icon ? <OlIcon name={option.icon} /> : null}
                                    {delayed ? <span className={font.tlnum}>{`(${delayed})`}</span> : null}
                                </ActionButton>
                            );
                        }
                        return null;
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
